import * as ToastPrimitive from "@radix-ui/react-toast";
import * as Portal from "@radix-ui/react-portal";
import styles from "./Toast.module.scss";
import {
  IconCheckmark,
  IconClose,
  IconException,
  IconExclude,
  IconProps,
  IconQuestionMark,
} from "../icons";
import { Button, ButtonVariant } from "../buttons";
import {
  ComponentType,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { useWindowSize } from "../../helpers/window-size/windowsize";

export type ToastVariant = "info" | "success" | "warning" | "error";
export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";
export type ToastSensitivity = "foreground" | "background";

export interface ToastActionProps {
  /** The label for the action button within the toast */
  label: string;
  /** Describes an alternative way of actioning the toast to screen reader users. This must be provided if an action button is used */
  altText: string;
  /** A callback function to be executed when the action button is pressed. */
  onClick: () => void;
}

export interface ToastProps {
  /** The visual appearance of the toast, this impacts the background and text color. If icon is not defined, it will also determine the default icon to display. */
  variant: ToastVariant;
  /** An optional icon to display within the toast, this will override the default icon provided by each variant. Explicitly setting this as null will not display any icon */
  icon?: ComponentType<IconProps> | null;
  /** The duration (in milliseconds) for which the toast will be displayed, this will override the default duration set by the Toaster. */
  duration?: number;
  /** An optional title for the toast. This appears before the body text, and has a heavier font weight. */
  title?: string;
  /** The main content or message of the toast. */
  body?: string;
  /** An optional mapping that holds the label, alternative text, and onClick callback for the action button in the toast.*/
  action?: ToastActionProps;
  /** A callback function to be executed when the toast is dismissed. This occurs when the toast is closed by clicking the close button. */
  onDismiss?: () => void;
  /** The sensitivity of the toast (for accessibility purposes). For toasts that are the result of a user action, choose foreground.
   * Toasts generated from background tasks should use background.
   * @default foreground
   */
  sensitivity?: ToastSensitivity;

  /** A callback function to be executed when the toast is to be disposed*/
  onDispose?: () => void;
}

export interface ToasterProps {
  /** The duration (in milliseconds) for which toasts will be displayed */
  duration: number;
  /** The position where the toasts will be displayed on the screen*/
  position: ToastPosition;
  /** Children, this should wrap the rest of the react application so that toasts can be called from anywhere in the application*/
  children: ReactNode;
}

// Define toast props without the variant to avoid declaration redundancy when using any of the dispatch methods
type ToastPropsWithoutVariant = Omit<ToastProps, "variant">;

type NotificationContextType = {
  info: (args: ToastPropsWithoutVariant) => void;
  success: (args: ToastPropsWithoutVariant) => void;
  warning: (args: ToastPropsWithoutVariant) => void;
  error: (args: ToastPropsWithoutVariant) => void;
};

export const NotificationContext = createContext<
  NotificationContextType | undefined
>(undefined);
export const ToastPositionContext = createContext<ToastPosition | undefined>(
  undefined,
);

export const Toaster = (props: ToasterProps) => {
  const [notifications, setNotifications] = useState<Map<string, ToastProps>>(
    new Map(),
  );
  const windowSize = useWindowSize();
  const [isMobile, setIsMobile] = useState(windowSize.width <= 768);
  const handleAddToast = useCallback((toast: ToastProps) => {
    setNotifications((prev) => {
      const newMap = new Map(prev);
      newMap.set(String(Date.now()), { ...toast });
      return newMap;
    });
  }, []);

  const handleRemoveToast = useCallback((key: string) => {
    setNotifications((prev) => {
      const newMap = new Map(prev);
      setTimeout(() => {
        newMap.delete(key);
      }, 150);
      return newMap;
    });
  }, []);

  const handleDispatchInfo = useCallback(
    (args: ToastPropsWithoutVariant) =>
      handleAddToast({ ...args, variant: "info" }),
    [handleAddToast],
  );

  const handleDispatchSuccess = useCallback(
    (args: ToastPropsWithoutVariant) =>
      handleAddToast({ ...args, variant: "success" }),
    [handleAddToast],
  );

  const handleDispatchWarning = useCallback(
    (args: ToastPropsWithoutVariant) =>
      handleAddToast({ ...args, variant: "warning" }),
    [handleAddToast],
  );

  const handleDispatchError = useCallback(
    (args: ToastPropsWithoutVariant) =>
      handleAddToast({ ...args, variant: "error" }),
    [handleAddToast],
  );

  useEffect(() => {
    setIsMobile(windowSize.width <= 768);
  }, [windowSize]);

  const toastPosition = `position-${
    isMobile ? "bottom-center" : props.position
  }`;

  /**
   * The provider for creating toasts, this component must be present in the top level of the application in order for toasts to appear.
   */
  return (
    <>
      <NotificationContext.Provider
        value={useMemo(
          () => ({
            info: handleDispatchInfo,
            success: handleDispatchSuccess,
            warning: handleDispatchWarning,
            error: handleDispatchError,
          }),
          [
            handleDispatchInfo,
            handleDispatchSuccess,
            handleDispatchWarning,
            handleDispatchError,
          ],
        )}
      >
        <ToastPositionContext.Provider value={props.position}>
          <ToastPrimitive.Provider duration={props.duration}>
            {props.children}

            {Array.from(notifications).map(([key, notifications]) => {
              return (
                <Toast
                  {...notifications}
                  key={key}
                  onDispose={() => handleRemoveToast(key)}
                />
              );
            })}

            <Portal.Root>
              <ToastPrimitive.Viewport
                className={`${styles.toastViewport} ${styles[toastPosition]}`}
              />
            </Portal.Root>
          </ToastPrimitive.Provider>
        </ToastPositionContext.Provider>
      </NotificationContext.Provider>
    </>
  );
};

/**
 * A customizable toast component for displaying notifications.


 * Disappears after the specified duration; This countdown will pause if the window is not in focus or the toast is being hovered or focused.


 * Primary use should include confirmation messages, simple notifications, and low-priority alerts that do not need to completely interrupt the user experience.
 * Use sparingly, as displaying too many notifications can over overwhelm the user.

 * Do not use the Toast for critical alerts that require immediate user attention; consider using modal dialogs or other prominent UI for such cases.

 * The react app and any subsequent children need to be wrapped in the Toaster component in order for toasts to render.

 * More information can be found here: https://www.radix-ui.com/primitives/docs/components/toast
 */
export const Toast = (props: ToastProps) => {
  const {
    variant,
    icon,
    duration,
    title,
    body,
    action,
    onDismiss,
    sensitivity = "foreground",
    onDispose,
  } = props;

  const position = useContext(ToastPositionContext);
  const windowSize = useWindowSize();
  const [isMobile, setMobile] = useState(windowSize.width <= 768);

  const toastAction = () => (
    <>
      {action && (
        <ToastPrimitive.Action asChild altText={action.altText}>
          <Button variant="tertiary-blue" onClick={action.onClick}>
            {action.label}
          </Button>
        </ToastPrimitive.Action>
      )}
    </>
  );

  const GetToastIcon = () => {
    if (props.icon) return <props.icon />;

    switch (variant) {
      case "info":
        return <IconQuestionMark />;
      case "success":
        return <IconCheckmark />;
      case "warning":
        return <IconException />;
      case "error":
        return <IconExclude />;
      default:
        return <></>;
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (onDispose) onDispose();
    }
  };

  const handleDismiss = () => {
    if (onDismiss) onDismiss();
  };

  const containerRef = useRef<HTMLLIElement>(null);
  const [isMultipleLines, setIsMultipleLines] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    setIsMultipleLines(containerRef.current.clientHeight > 60);
  }, [containerRef]);

  useEffect(() => {
    setMobile(windowSize.width <= 768);
  }, [windowSize]);

  const rootClasses = classNames(
    styles.toastRoot,
    styles[`background-${variant}`],
  );

  const contentClasses = classNames(
    styles.toastContainer,
    styles[`text-${variant}`],
    {
      [styles.toastRootAlignStart]: isMultipleLines,
      [styles.toastRootAlignCenter]: !isMultipleLines,
    },
  );

  const closeButtonClasses = classNames(styles.toastCloseButton, {
    [styles.toastCloseButtonOffset]: isMultipleLines,
  });

  const closeIconClasses = classNames(
    styles.toastCloseIcon,
    styles[`background-${variant}`],
  );

  return (
    <>
      <ToastPrimitive.Root
        type={sensitivity}
        onSwipeEnd={(event) => event.preventDefault()}
        duration={duration}
        defaultOpen={true}
        onEscapeKeyDown={handleDismiss}
        onOpenChange={handleOpenChange}
        className={rootClasses}
        data-position={isMobile ? "bottom-center" : position}
        ref={containerRef}
      >
        <div className={contentClasses}>
          {icon !== null && (
            <div className={styles.toastIcon}>
              <GetToastIcon />
            </div>
          )}

          <div className={styles.toastContent}>
            {title && (
              <ToastPrimitive.Title className={styles.toastTitle}>
                <span>{title}</span>
              </ToastPrimitive.Title>
            )}

            {body && (
              <ToastPrimitive.Description className={styles.toastDescription}>
                <span>{body}</span>
              </ToastPrimitive.Description>
            )}
          </div>

          <div className={`${styles.toastActions}`}>
            {!isMultipleLines && toastAction()}

            <ToastPrimitive.Close
              onClick={handleDismiss}
              aria-label="Close"
              className={closeButtonClasses}
            >
              <IconClose className={closeIconClasses} />
            </ToastPrimitive.Close>
          </div>
        </div>

        {isMultipleLines && (
          <div className={styles.toastActionAsFooter}>{toastAction()}</div>
        )}
      </ToastPrimitive.Root>
    </>
  );
};

export function useToast() {
  const context = useContext(NotificationContext);
  if (context) return context;
  throw new Error("useNotification must be used within Notifications");
}
